// src/pages/AuthMigration.tsx
//
// Zitadel → Better Auth migration, run by an operator.
//
// The shape of this page is deliberately a one-way ratchet: preview, then dry
// run, then commit. Each step unlocks the next and nothing writes until the
// last one. An import that writes on the first click is one mistimed press away
// from rewriting credentials for a company nobody meant to touch.
//
// The number that decides whether a cutover is acceptable is
// `needsPasswordReset` — those people get a reset email instead of a seamless
// move. It is shown as prominently as the success count on purpose, because it
// is the cost of the migration and it is the thing that generates support load.

import { useEffect, useState } from 'react';
import {
  ArrowRightIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  KeyIcon,
  ShieldExclamationIcon,
} from '@heroicons/react/24/outline';
import api from '../api/axios';

interface Summary {
  accounts: number;
  memberships: number;
  companies: number;
  multiCompany: number;
  skippedBadEmail: number;
  skippedSuspendedCompany: number;
  signInUnchanged: number;
  needsPasswordReset: number;
}

interface ZitadelStatus {
  reachable: boolean;
  reason?: string;
  exportedUsers?: number;
  matched?: number;
  unmatchedLocal?: number;
  orphanedInZitadel?: number;
}

interface Company {
  id: string;
  company_name: string;
  company_code: string;
}

interface ImportResult {
  commit: boolean;
  considered: number;
  created: number;
  linkedPassword: number;
  skippedExisting: number;
  skippedNoPassword: number;
  skippedUnknownHash: number;
  errors: Array<{ email: string; error: string }>;
}

function Stat({ label, value, tone = 'slate', sub }: { label: string; value: React.ReactNode; tone?: 'slate' | 'green' | 'amber' | 'red'; sub?: string }) {
  const tones = {
    slate: 'border-slate-200 bg-white text-slate-900',
    green: 'border-emerald-200 bg-emerald-50 text-emerald-800',
    amber: 'border-amber-200 bg-amber-50 text-amber-800',
    red: 'border-red-200 bg-red-50 text-red-800',
  } as const;
  return (
    <div className={`rounded-xl border p-4 ${tones[tone]}`}>
      <div className="text-[11px] font-bold uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-2xl font-bold tabular-nums">{value}</div>
      {sub && <div className="mt-0.5 text-[11px] opacity-70">{sub}</div>}
    </div>
  );
}

export default function AuthMigration() {
  const [token, setToken] = useState('');
  const [orgIds, setOrgIds] = useState('');
  const [summary, setSummary] = useState<Summary | null>(null);
  const [zitadel, setZitadel] = useState<ZitadelStatus | null>(null);
  const [dryRun, setDryRun] = useState<ImportResult | null>(null);
  const [committed, setCommitted] = useState<ImportResult | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cutCompanyId, setCutCompanyId] = useState('');
  const [cutResult, setCutResult] = useState<string | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);

  // Companies are picked from a list, not typed. The cutover takes a
  // registry_companies.id — a UUID nobody has memorised — and pasting the wrong
  // one switches the login for a company you did not mean to touch.
  useEffect(() => {
    let alive = true;
    api.get('/super-admin/companies')
      .then((r) => { if (alive) setCompanies(r.data?.companies ?? []); })
      .catch(() => { /* the field falls back to free text below */ });
    return () => { alive = false; };
  }, []);

  const orgIdList = orgIds.split(',').map((s) => s.trim()).filter(Boolean);

  function fail(err: any, fallback: string) {
    const code = err?.response?.data?.code;
    setError(
      code === 'reauth_required'
        ? 'Re-authentication required — sign in again, then retry. Destructive actions need a recent sign-in.'
        : code === 'two_factor_required'
          ? 'Enrol two-factor on your operator account before running a migration.'
          : err?.response?.data?.message || err?.response?.data?.error || fallback
    );
  }

  async function runPreview() {
    setBusy('preview'); setError(null); setSummary(null); setDryRun(null); setCommitted(null);
    try {
      const res = await api.post('/super-admin/account/migration/preview', {
        zitadel_token: token, org_ids: orgIdList,
        company_id: cutCompanyId || null,
      });
      setSummary(res.data.summary);
      setZitadel(res.data.zitadel ?? null);
    } catch (err) { fail(err, 'Preview failed.'); } finally { setBusy(null); }
  }

  async function runImport(commit: boolean) {
    setBusy(commit ? 'commit' : 'dry'); setError(null);
    try {
      const res = await api.post('/super-admin/account/migration/import', {
        zitadel_token: token, org_ids: orgIdList, commit,
        company_id: cutCompanyId || null,
      });
      if (commit) setCommitted(res.data); else { setDryRun(res.data); setCommitted(null); }
    } catch (err) { fail(err, commit ? 'Import failed.' : 'Dry run failed.'); } finally { setBusy(null); }
  }

  async function switchProvider(provider: 'zitadel' | 'better-auth') {
    setBusy(`cut-${provider}`); setError(null); setCutResult(null);
    try {
      const res = await api.post(`/super-admin/account/companies/${encodeURIComponent(cutCompanyId)}/auth-provider`, {
        auth_provider: provider,
      });
      setCutResult(
        `${res.data.company_code} now uses ${provider === 'better-auth' ? 'the new login' : 'the Zitadel redirect'}.`
      );
    } catch (err) { fail(err, 'Could not switch this company.'); } finally { setBusy(null); }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <div className="sticky top-0 z-10 border-b border-slate-200 bg-white/80 backdrop-blur-md">
        <div className="mx-auto max-w-5xl px-6 py-5">
          <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900">
            <KeyIcon className="h-7 w-7 text-blue-600" />
            Auth migration
          </h1>
          <p className="mt-0.5 text-sm text-slate-500">
            Move users and password hashes out of Zitadel. Nothing is written until you commit.
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-5xl space-y-6 px-6 py-6">
        {error && (
          <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Step 1 — credentials */}
        <section className="rounded-xl border border-slate-200 bg-white p-5">
          <h2 className="text-sm font-bold text-slate-900">1 · Read the accounts</h2>
          <p className="mt-0.5 text-[12.5px] text-slate-500">
            Accounts come from <span className="font-mono">registry_company_admin_users</span> — already authoritative
            for who exists and which company they belong to. Zitadel is used only for password hashes, with the
            server&apos;s configured <span className="font-mono">ZITADEL_PAT</span>. Override it only if that token
            lacks export scope.
          </p>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="zt" className="text-[11.5px] font-semibold text-slate-600">
                Zitadel token <span className="font-normal text-slate-400">(optional override)</span>
              </label>
              <input
                id="zt" type="password" value={token} onChange={(e) => setToken(e.target.value)}
                placeholder="leave blank to use ZITADEL_PAT"
                className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label htmlFor="cut-company" className="text-[11.5px] font-semibold text-slate-600">
                Company <span className="font-normal text-slate-400">(blank = every company)</span>
              </label>
              {companies.length > 0 ? (
                <select
                  id="cut-company" value={cutCompanyId} onChange={(e) => setCutCompanyId(e.target.value)}
                  className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm outline-none focus:border-blue-500"
                >
                  <option value="">All companies</option>
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>{c.company_name} ({c.company_code})</option>
                  ))}
                </select>
              ) : (
                <input
                  id="cut-company" value={cutCompanyId} onChange={(e) => setCutCompanyId(e.target.value)}
                  placeholder="registry_companies.id"
                  className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm outline-none focus:border-blue-500"
                />
              )}
            </div>
            <div>
              <label htmlFor="orgs" className="text-[11.5px] font-semibold text-slate-600">
                Zitadel org IDs <span className="font-normal text-slate-400">(optional, blank = all)</span>
              </label>
              <input
                id="orgs" value={orgIds} onChange={(e) => setOrgIds(e.target.value)}
                placeholder="leave blank for every org"
                className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm outline-none focus:border-blue-500"
              />
            </div>
          </div>
          <button
            type="button" disabled={busy !== null} onClick={runPreview}
            className="mt-4 flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
          >
            {busy === 'preview' ? 'Reading…' : 'Preview export'} <ArrowRightIcon className="h-4 w-4" />
          </button>
        </section>

        {/* Step 2 — what actually comes across */}
        {summary && (
          <section className="rounded-xl border border-slate-200 bg-white p-5">
            <h2 className="text-sm font-bold text-slate-900">2 · What comes across</h2>
            <p className="mt-0.5 text-[12.5px] text-slate-500">
              {summary.accounts} accounts across {summary.companies} companies
              {summary.multiCompany > 0 && ` · ${summary.multiCompany} administer more than one`}.
            </p>

            <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
              <Stat label="Sign in unchanged" value={summary.signInUnchanged} tone="green" sub="password hash matched" />
              <Stat label="Need a reset email" value={summary.needsPasswordReset} tone={summary.needsPasswordReset ? 'amber' : 'slate'} sub="no usable hash" />
              <Stat label="Companies" value={summary.companies} sub={`${summary.memberships} memberships`} />
              <Stat label="Skipped" value={summary.skippedBadEmail + summary.skippedSuspendedCompany} tone={summary.skippedBadEmail + summary.skippedSuspendedCompany ? 'amber' : 'slate'} sub="bad email / suspended" />
            </div>

            {zitadel && !zitadel.reachable && (
              <div className="mt-4 flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
                <ShieldExclamationIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
                <div className="text-[12px] leading-relaxed text-amber-800">
                  <strong>Zitadel not consulted: {zitadel.reason}.</strong> The migration can still run — every account
                  will be created, but nobody keeps their existing password. All {summary.accounts} would need a reset
                  email. Fix the connection first unless that is genuinely what you want.
                </div>
              </div>
            )}

            {zitadel?.reachable && (
              <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3 text-[12px] text-slate-700">
                Zitadel returned {zitadel.exportedUsers} users; {zitadel.matched} matched an account here.
                {zitadel.orphanedInZitadel ? ` ${zitadel.orphanedInZitadel} exist in Zitadel but have no company membership here and are deliberately not imported.` : ''}
              </div>
            )}

            <button
              type="button" disabled={busy !== null} onClick={() => runImport(false)}
              className="mt-4 flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 disabled:opacity-50"
            >
              {busy === 'dry' ? 'Running…' : 'Dry run import'} <ArrowRightIcon className="h-4 w-4" />
            </button>
          </section>
        )}

        {/* Step 3 — dry run, then commit */}
        {dryRun && (
          <section className="rounded-xl border border-slate-200 bg-white p-5">
            <h2 className="text-sm font-bold text-slate-900">3 · Dry run result</h2>
            <p className="mt-0.5 text-[12.5px] text-slate-500">Nothing was written. This is what committing would do.</p>

            <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
              <Stat label="Accounts created" value={dryRun.created} tone="green" />
              <Stat label="Passwords linked" value={dryRun.linkedPassword} tone="green" />
              <Stat label="Already present" value={dryRun.skippedExisting} sub="left untouched" />
              <Stat label="Errors" value={dryRun.errors.length} tone={dryRun.errors.length ? 'red' : 'slate'} />
            </div>

            {dryRun.errors.length > 0 && (
              <div className="mt-3 max-h-40 overflow-y-auto rounded-lg border border-red-200 bg-red-50 p-3 text-[12px] text-red-800">
                {dryRun.errors.map((e, i) => (
                  <div key={i}><span className="font-semibold">{e.email}</span> — {e.error}</div>
                ))}
              </div>
            )}

            {!committed && (
              <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3">
                <p className="text-[12.5px] text-red-800">
                  Committing writes credentials into the registry. It is idempotent and will not overwrite a password
                  someone has already changed inside CITS, but it cannot be undone from this page.
                </p>
                <button
                  type="button" disabled={busy !== null}
                  onClick={() => {
                    if (window.confirm(`Import ${dryRun.created} accounts and ${dryRun.linkedPassword} passwords for real?`)) {
                      void runImport(true);
                    }
                  }}
                  className="mt-3 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                >
                  {busy === 'commit' ? 'Importing…' : 'Commit import'}
                </button>
              </div>
            )}
          </section>
        )}

        {/* Step 4 — the switch. Separate from the import on purpose: importing
            makes the new login possible, this is what makes it happen. */}
        {(committed || summary) && (
          <section className="rounded-xl border border-slate-200 bg-white p-5">
            <h2 className="text-sm font-bold text-slate-900">4 · Switch a company over</h2>
            <p className="mt-0.5 text-[12.5px] text-slate-500">
              Importing users changes nothing on its own. This is the cutover — and switching back is the rollback.
            </p>
            <div className="mt-4 flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[240px]">
                <div className="text-[11.5px] font-semibold text-slate-600">Company</div>
                <div className="mt-1 flex h-10 items-center rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm text-slate-700">
                  {cutCompanyId
                    ? (companies.find((c) => c.id === cutCompanyId)?.company_name ?? cutCompanyId)
                    : 'Pick a company in step 1 — switching cannot be applied to all at once.'}
                </div>
              </div>
              <button
                type="button" disabled={!cutCompanyId || busy !== null}
                onClick={() => void switchProvider('better-auth')}
                title={cutCompanyId ? undefined : 'Select a company in step 1 first'}
                className="h-10 rounded-lg bg-emerald-600 px-4 text-sm font-semibold text-white disabled:opacity-50"
              >
                {busy === 'cut-better-auth' ? 'Switching…' : 'Use new login'}
              </button>
              <button
                type="button" disabled={!cutCompanyId || busy !== null}
                onClick={() => void switchProvider('zitadel')}
                className="h-10 rounded-lg border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 disabled:opacity-50"
              >
                {busy === 'cut-zitadel' ? 'Reverting…' : 'Revert to Zitadel'}
              </button>
            </div>
            {cutResult && (
              <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-[12.5px] text-emerald-800">
                {cutResult}
              </div>
            )}
            <p className="mt-3 text-[11.5px] text-slate-500">
              The server refuses to switch a company whose admins have not been imported — that would lock everyone
              out at once.
            </p>
          </section>
        )}

        {committed && (
          <section className="rounded-xl border border-emerald-200 bg-emerald-50 p-5">
            <h2 className="flex items-center gap-2 text-sm font-bold text-emerald-900">
              <CheckCircleIcon className="h-5 w-5" /> Import committed
            </h2>
            <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
              <Stat label="Created" value={committed.created} tone="green" />
              <Stat label="Passwords linked" value={committed.linkedPassword} tone="green" />
              <Stat label="Need reset email" value={committed.skippedNoPassword} tone={committed.skippedNoPassword ? 'amber' : 'slate'} />
              <Stat label="Errors" value={committed.errors.length} tone={committed.errors.length ? 'red' : 'slate'} />
            </div>
            <p className="mt-4 text-[12.5px] text-emerald-900">
              These users can now sign in on their tenant portal with the password they already had. Zitadel still works —
              nothing has been switched off, so you can cut over one company at a time.
            </p>
          </section>
        )}
      </div>
    </div>
  );
}
